/**
 * PayFast "Buy Now" styled top-up for AI credits.
 *
 * IMPORTANT: A raw PayFast Buy Now form posts directly to PayFast with no
 * notify_url or custom fields, so our ITN webhook cannot tie the payment back
 * to a specific user — credits would NOT auto-grant. This component preserves
 * the merchant's amount × quantity UX but routes the submit through our
 * server (`orderCreditPack` + signed PayFast checkout) so:
 *   1. PayFast notifies our site via the ITN webhook on success
 *   2. The matching credit_orders row is marked paid
 *   3. Credits are granted to the user's account automatically
 */
import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { orderCreditPack } from "@/lib/api/credits.functions";
import { supabase } from "@/integrations/supabase/client";
import {
  ADMIN_FEE_ZAR,
  MIN_CREDIT_SPEND_ZAR,
  PRICE_PER_CREDIT_ZAR,
} from "@/lib/credit-packs";
import { computeCreditBreakdown } from "@/lib/credit-rounding";
import { CreditBreakdownPanel } from "@/components/site/CreditBreakdownPanel";

export function PayFastBuyNowCredits() {
  const minTotal = MIN_CREDIT_SPEND_ZAR + ADMIN_FEE_ZAR; // R30
  const [amount, setAmount] = useState<number>(minTotal);
  const [qty, setQty] = useState<number>(1);
  const [busy, setBusy] = useState(false);
  const orderPack = useServerFn(orderCreditPack);
  const navigate = useNavigate();

  const requestedTotal = Math.max(0, Number(amount) || 0) * Math.max(1, Number(qty) || 1);
  const breakdown = computeCreditBreakdown(requestedTotal);
  const credits = breakdown.credits;
  const totalZar = breakdown.totalZar;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!breakdown.meetsMinimum) {
      toast.error(`Minimum top-up is R${minTotal.toFixed(2)} (includes R${ADMIN_FEE_ZAR} admin fee).`);
      return;
    }
    setBusy(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.info("Please sign in to top up credits.");
        navigate({ to: "/auth", search: { redirect: "/credits" } as any });
        return;
      }
      const r = await orderPack({ data: { customCredits: credits } });
      toast.success("Redirecting to PayFast…");
      const { launchPayfastCheckout } = await import("@/lib/payfast-launch");
      await launchPayfastCheckout(r.reference);
    } catch (err: any) {
      toast.error("Could not start checkout", { description: err?.message ?? String(err) });
    } finally {
      setBusy(false);
    }
  }



  return (
    <div className="mt-8 rounded-2xl border border-border bg-card p-6 shadow-soft">
      <h3 className="font-display text-lg text-navy">Quick top-up — pay any amount</h3>
      <p className="mt-1 text-xs text-muted-foreground">
        Enter any amount from R{minTotal.toFixed(2)} (includes the R{ADMIN_FEE_ZAR} admin fee).
        Credits are issued at R{PRICE_PER_CREDIT_ZAR.toFixed(2)} each and added to your account
        automatically once PayFast confirms the payment.
      </p>
      <form onSubmit={onSubmit} className="mt-4 space-y-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block text-xs">
            <span className="font-semibold uppercase tracking-wider text-muted-foreground">Amount (ZAR)</span>
            <input
              required
              type="number"
              step="0.01"
              min={minTotal}
              value={amount}
              onChange={(e) => setAmount(Number(e.target.value))}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
          </label>
          <label className="block text-xs">
            <span className="font-semibold uppercase tracking-wider text-muted-foreground">Quantity</span>
            <input
              required
              type="number"
              min={1}
              value={qty}
              onChange={(e) => setQty(Number(e.target.value))}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
          </label>
        </div>

        <CreditBreakdownPanel breakdown={breakdown} />

        <button
          type="submit"
          disabled={busy || !breakdown.meetsMinimum}
          className="inline-flex items-center gap-2 rounded-md bg-[#13B5EA] px-4 py-2 text-sm font-semibold text-white hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          Buy Now with PayFast
        </button>
        <p className="text-[11px] text-muted-foreground">
          You'll be redirected to PayFast's secure page. We're notified via PayFast ITN the
          moment your payment is confirmed, and your credit balance updates automatically.
        </p>
      </form>
    </div>
  );
}
